/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
*/

import { MatDialog } from "@angular/material/dialog";
import moment from "moment";
import { Observable, of } from "rxjs";

import { AbstractObject, TenantId, uuid } from "@src/app/model/abstract-object";
import { Action } from "@src/app/model/action";
import { ActionGridComponent } from "@src/app/shared/action/action-grid.component";

import { User } from "@src/app/model/user";

import { ConfirmDialogService } from "@src/app/shared/dialogs/confirmDialog";
import { EditDialogComponent } from "@src/app/shared/dialogs/edit-dialog/edit-dialog.component";
import { AppFormControl } from "@src/app/shared/form/app-form-control";
import { FieldSet } from "@src/app/shared/form/field-set/field-set.component";
import { FormButtonComponent } from "@src/app/shared/form/form-button/form-button.component";
import { FormDateTimeComponent } from "@src/app/shared/form/form-date-time/form-date-time.component";
import { FormDateComponent } from "@src/app/shared/form/form-date/form-date.component";
import { FormDurationComponent } from "@src/app/shared/form/form-duration/form-duration.component";
import { FormPicklistComponent } from "@src/app/shared/form/form-picklist/form-picklist.component";
import { FormTextAreaComponent } from "@src/app/shared/form/form-text-area/form-text-area.component";
import { FormTextComponent } from "@src/app/shared/form/form-text/form-text.component";
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridControl } from "@src/app/shared/grid/grid-control";
import { GridField } from "@src/app/shared/grid/grid-field";
import { required } from "@src/app/shared/validators";
import { makeItemOwnerField } from "@src/app/shared/field/ItemOwnerField";
import { GridControlParameters } from "@src/app/shared/grid/grid-control-parameters";
import { FormGroup } from "@angular/forms";
import { Field } from "@src/app/shared/field/Field";
import { FieldMaker } from "@src/app/shared/field/FieldMaker";
import { ActionService } from "@src/app/modules/crm/action.service";
import { MyInjector } from "@src/app/app.module";
import { Omc } from '@src/app/model/Omc';
import { AgentDataService } from "@src/app/modules/agent/agent-data.service";
import { Unit } from "@src/app/model/unit";
import { Person } from "@src/app/model/person";
import { FormRichTextComponent } from "@src/app/shared/form/form-rich-text/form-rich-text.component";
import { CurrentUserService } from "@src/app/modules/user/current-user.service";
import { MessageService } from "@src/app/shared/message.service";
import { UserService } from "@src/app/modules/user/user.service";
import { AttachmentListComponent } from "@src/app/shared/form/file/attachment-list/attachment-list.component";
import { ActionType } from "@src/app/shared/action/add-action-form-action";
import { tap } from "rxjs/operators";

export type LinksTo = 'Request' | 'Unit' | 'Person' | 'Supplier' | 'FixedAsset'
export class ActionGridField extends GridField {
    requestId: uuid;
    unitId: uuid;
    personId: uuid;
    supplierId: uuid;
    fassetId: uuid;
    linksTo: LinksTo;
    omcId: uuid;
    teamId: TenantId;

    attachFromAttribute = 'id';

    actionService = MyInjector.instance.get(ActionService);
    dialog = MyInjector.instance.get(MatDialog);
    cds = MyInjector.instance.get(ConfirmDialogService);
    currentUser = MyInjector.instance.get(CurrentUserService).currentUser;
    userService = MyInjector.instance.get(UserService);
    msgSvc = MyInjector.instance.get(MessageService);

    omcs: Omc[] = [];

    tabIcon = 'lists';

    /** Want to be able to refresh the request screen, as the status is changed by the change to the action... */
    actionCompleteFn: () => void;

    readonly completedText = 'check_box_outline';
    readonly notCompletedText = 'check_box_outline_blank';

    /*
     * Simple view - a stripped-back list instead of the full grid, driven entirely by the flags
     * below. Off by default; only pages that call showSimpleVersion() get it, so the full grid
     * used everywhere else is untouched. actionList is the plain array the simple template renders
     * (the same Action objects the grid rows are built from).
     */
    showSimple = false;
    allowSimpleAdd = true;
    allowAdd = true;
    actionList: Action[] = [];

    units: Unit[] = [];
    people: Person[] = [];
    users: User[] = [];

    constructor(options: Partial<Field>) {
        const field = new Field({
            name: 'actions',
            label: 'Actions',
            value: 'actions',
            type: 'grid',
            sendServer: false,
            visible: Field.formOnly,
        }, options);

        const parms: GridControlParameters = {
            field,
            rowFactory: () => [],
        }
        super(parms);

        this.getTabChip = this.getActionChip;

        this.userService.getAgentUsers().subscribe((users: User[]) => this.users = users);
    }

    setValue(item: AbstractObject, readonly: boolean) {
        if (this.linksTo === 'Request') {
            this.requestId = item.id;
        } else if (this.linksTo === 'Person') {
            this.personId = item.id;
            this.units.length = 0;
            const p = item as Person;
            p.units.forEach(pur => this.units.push(pur.unit));
            if (this.units.length === 1) {
                this.unitId = this.units[0].id;
            }
        } else if (this.linksTo === 'Supplier') {
            this.supplierId = item.id;
        } else if (this.linksTo === 'Unit') {
            this.unitId = item.id;
            this.people.length = 0;
            const u = item as Unit;
            u.people.forEach(pur => this.people.push(pur.person));
            if (item['ownerId']) {
                this.personId = item['ownerId'];
            }
        } else if (this.linksTo === 'FixedAsset') {
            this.fassetId = item.id
        }

        this.omcId = item['omcId'];
        this.teamId = item['teamId'];
        this.actionList = (this.getValue(item) as Action[]) || [];
        return super.setValue(item, readonly);
    }

    showSimpleVersion({ adds = true }: { adds?: boolean } = {}) {
        this.showSimple = true;
        this.allowSimpleAdd = adds;
        return this;
    }

    /**
     * Existing actions still open and complete; only adding is off.
     *
     * For a parent the agency owns rather than an OMC - a supplier - where the grid's teamId is the
     * agent team, so a new action would belong to no OMC and OurModel would refuse it.
     */
    noAdd() {
        this.allowAdd = false;
        return this;
    }

    makeTabControl(relatedFormGroup: FormGroup) {
        this.relatedFormGroup = relatedFormGroup;
        return this.makeControl();
    }

    makeControl() {
        this.config.rowFactory = this.rowFactory.bind(this);
        // Add is done by the two buttons in action-grid.component (Add reminder / Log completed),
        // not the grid's built-in single new-row button - so no objFactory/newOptionText here.
        if (this.currentUser.inAgency) {
            MyInjector.instance.get(AgentDataService).getOmcList().subscribe(omcs => {
                this.omcs.length = 0;
                omcs.forEach((omc: Omc) => this.omcs.push(omc));
            });
        }
        this.control = new GridControl(this);
        // Reset it, grid control overrides it...
        this.formControlFactory = ActionGridComponent.createComponent;

        return this.control;
    }
    /* This is called with null on new actions...
       The actual action object we edit will however be a copy fresh from server if editing
       or created later if a new action...
    */
    private getActionFields(action: Action): Field[] {
        const flds = [];

        const omcHolder = FieldMaker.idHolder('omcId');

        if (this.linksTo === 'Person') {
            const unitField = FormPicklistComponent.make('Related Unit', 'unitId', null, {
                items: this.units, allowSelectNone: true, optionDisplayValue: o => Unit.getFullName(o),
            }, { hint: 'If you wish to link the action to persons unit, select here, otherwise leave blank' });
            flds.push(unitField);
        } else {
            flds.push(FieldMaker.idHolder('unitId'));
        }

        if (this.linksTo === 'Unit') {
            const personField = FormPicklistComponent.make('Related Person', 'personId', null, {
                items: this.people, allowSelectNone: true, optionDisplayValue: (o) => o?.fullName,
            }, { hint: 'If you wish to link the action to persons, select here, otherwise leave blank' });
            flds.push(personField);
        } else {
            flds.push(FieldMaker.idHolder('personId'));
        }

        flds.push(FieldMaker.id());
        flds.push(FieldMaker.rev());
        flds.push(FieldMaker.idHolder('requestId'));
        flds.push(FieldMaker.idHolder('supplierId'));
        flds.push(FieldMaker.idHolder('fassetId'));
        flds.push(FieldMaker.idHolder('teamId'));
        flds.push(omcHolder);

        flds.push(FormTextComponent.make('Title', 'title', { validators: [required], }));
        flds.push(FormDateComponent.make('Planned Date', 'plannedDate',
            { formColumn: 2, validators: [required] })
        );
        // The agency's staff and the OMC's members, one list - the old combo offered agency only,
        // so an action could never be handed to the OMC member who was going to do it.
        flds.push(makeItemOwnerField({ formColumn: 2, cellOpts: { minWidth: '60px' } }));

        flds.push(FormDateTimeComponent.make('Completed', 'completedAt', { formColumn: 2 }));
        /* calculateValue: (a) => (a as Action).completedAt */

        flds.push(FormDurationComponent.make('Time Spent', 'timeSpent', { formColumn: 2 }));

        flds.push(FormRichTextComponent.make('Description', 'description', { cellOpts: { width: '50%' } }));

        if (action?.template) {
            flds.push(FormTextAreaComponent.make('Action Instructions', 'template.description',
                { readonly: true, cellOpts: { width: '50%' }, sendServer: false, disable: true }
            ));
        }

        const af = AttachmentListComponent.make('Attach File', 'attachments',
            { service: this.actionService, allowNew: true, smallAddBox: true },
            { formColumn: 2 }
        );
        if (action?.completedAt || this.readonly) {
            af.readonly = true;
            af.fileOpts.allowNew = false;
        }
        flds.push(af)
        return flds;
    }

    private rowFactory(action: Action = null): Field[] {
        const flds = [];

        if (!this.currentUser.inAgency) {
            flds.push(FormTextComponent.make('Agent', 'agentName', { readonly: true }));
        }
        //flds.push(FormButtonComponent.makeLink('Action', 'title', '/crm/actions/${id}'));
        const titleField = FormButtonComponent.makeTextButton('Title', 'title', (ctl: AppFormControl) => {
            this.upsertAction(action).subscribe(updated => {
                if (updated) {
                    const row = ctl.getRow();
                    row.refreshFrom(updated);
                }
            });
        });
        titleField.btnOpts.style = "background-color:blue";
        flds.push(titleField);

        flds.push(FormDateComponent.make('Planned Date', 'plannedDate', { readonly: true }));
        flds.push(FormTextComponent.make('Owned By', 'ownedByName', { readonly: true }));

        const readCheckbox = FormButtonComponent.make('Read', 'readAtCheckbox', {
            type: 'icon',
            calculateValue: (action) => (action as Action).ownerReadAt ? this.completedText : this.notCompletedText,
            disable: true,
        });
        flds.push(readCheckbox);

        flds.push(FormDateTimeComponent.make('Completed', 'completedAt', { readonly: true }));
        const completedAtCheckbox = FormButtonComponent.make('Done', 'completedAtCheckbox', {
            type: 'icon',
            calculateValue: (action) => (action as Action).completedAt ? this.completedText : this.notCompletedText,
            btnOpts: { clickMethod: this.markActionComplete.bind(this) }
        });

        const when = moment(action?.completedAt * 1000).fromNow();
        completedAtCheckbox.toolTip = action?.completedAt ? 'Completed : ' + when : 'Click to mark complete';
        flds.push(completedAtCheckbox);
        flds.push(FormDurationComponent.make('Time Spent', 'timeSpent', { readonly: true }));

        flds.push(FormTextAreaComponent.make('Description', 'description', { readonly: true }));

        if (action && action.template) {
            flds.push(FormTextAreaComponent.make('Action Instructions', 'template.description',
                { readonly: true, cellOpts: { width: '50%' }, sendServer: false, disable: true }
            ));
        }

        return flds;
    }

    markActionComplete(ctl: AppFormControl) {
        const row = ctl.getRow();
        this.completeAction(row.focus as Action, updated => row.refreshFrom(updated));
    }

    /**
     * Confirm, POST the completion, then hand the fresh action back to the caller to refresh
     * however it needs to (a grid row, or an object in the simple view's list).
     */
    completeAction(action: Action, onComplete: (updated: Action) => void) {
        if (this.readonly) {
            this.cds.alert('Permissions Required', 'You do not have permission to update status of this action');
            return;
        }
        if (action.completedAt) {
            this.cds.alert('Cannot Mark Undone',
                'Cannot undo completing an action, please add a comment to it or create a replacement action');
            return;
        }
        this.cds.open('Confirm Completed',
            'Mark this action completed? You cannot undo this action',
            () => {
                this.actionService.complete(action, this.currentUser).subscribe(updated => {
                    if (updated) {
                        onComplete(updated);
                        if (this.actionCompleteFn) {
                            this.actionCompleteFn();
                        }
                    }
                })
            }
        );
    }

    upsertAction(action: Action = null, actionType: ActionType = 'future') {
        if (this.relatedFormGroup.dirty) {
            this.cds.alert('Save First', 'Sorry, you need to save request before adding actions');
            return of(null);
        }

        // if (this.control.parent.dirty) {
        //     this.cds.alert('Save First', 'Sorry, you need to save before adding actions');
        //     return of(null);
        // }
        //if (this.page.form.formGroup.dirty) {
        //    this.cds.alert('Save First', 'Sorry, you need to save request before adding actions');
        //    return of(null);
        //}
        const fields = this.getActionFields(action);

        const dialogFormConfig = new FormConfig(
            {
                title: $localize`Action`,
                customTitle: action ? null : this.newActionTitle(actionType),
                fieldSet: new FieldSet(
                    {
                        fields: fields,
                        formLayout: [{ cells: [{ width: '66%' }, { width: '33%' }] }]
                    }
                ),
                mode: action ? 'edit' : 'new',
                objectFactory: this.createActionType(actionType),
                readonly: action?.completedAt > 0 ? true : false,
                allowEdit: !this.readonly,
                allowNew: !this.readonly
            }
        );

        const dialogRef = this.dialog.open(EditDialogComponent,
            {
                data:
                {
                    config: dialogFormConfig,
                    service: this.actionService,
                    id: action ? action.id : null,
                    hideTabs: true,
                    height: 820,
                }
            }
        );

        return dialogRef.afterClosed().pipe(tap(a => {
            if (this.actionCompleteFn) {
                this.actionCompleteFn();
            }
            return a;
        }));
        /*
        dialogRef.afterClosed().pipe(first()).pipe<Action>(map(o => {
            return o;
        }));
        */
    }

    private newActionTitle(actionType: ActionType): string {
        return actionType === 'past'
            ? $localize`Log Completed Action`
            : $localize`Add Reminder`;
    }

    createActionType(actionType: ActionType) {
        return () : Observable<Action> => {
            const a = new Action();
            a.requestId = this.requestId;
            a.unitId = this.unitId;
            a.supplierId = this.supplierId;
            a.fassetId = this.fassetId;
            a.personId = this.personId;
            a.omcId = this.omcId;
            a.teamId = this.teamId;
            if (actionType === 'past') {
                a.completedAt = new Date().getTime() / 1000;
            }
            // agentTeamId and omcId are stamped server side from the team's primary agent, so
            // nothing is set here. The OMC is looked up in the agency only to warn when the work
            // will be filed against an agency other than the one raising it.
            if (this.currentUser.inAgency) {
                const omc = this.omcs.find(o => o.omcTeamId === this.teamId);
                if (!omc) {
                    this.msgSvc.showErrorMessage($localize`This OMC does not seem to be configured`);
                } else if (omc.primaryAgentId && omc.primaryAgentId !== this.currentUser.current_team_id) {
                    this.msgSvc.warn($localize`This will be raised for ${omc.name}'s managing agency, not yours`);
                }
            }
            a.plannedDate = new Date().toISOString().substring(0, 10);
            a.ownedById = this.currentUser.id;

            return of(a);
        }
    }


    getActionChip(actions: Action[]): string {
        if (Array.isArray(actions)) {
            const total = actions.length;
            if (total === 0) {
                return '';
            }
            const done = actions.filter((a) => a.completedAt).length;
            return '' + (total - done) + '/' + total;
        }
        return '';
    }
}
